import { z } from 'zod';
export const workoutTypeSchema = z.enum([
    'ride',
    'run',
    'walk',
    'swim',
    'strength',
    'mobility',
    'row',
    'other',
]);
export const intensitySchema = z.enum(['easy', 'moderate', 'hard']);
export const syncProviderSchema = z.enum([
    'strava',
    'withings',
    'garmin',
    'healthkit',
    'health_connect',
    'manual',
]);
export const workoutTemplateSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid().nullable(),
    name: z.string(),
    type: workoutTypeSchema,
    durationMin: z.number().int(),
    intensity: intensitySchema,
    rpe: z.number().int().nullable(),
    met: z.number().nullable(),
    notes: z.string().nullable(),
    isStarter: z.boolean(),
    /** Estimated for the member asking, from their latest weight. */
    estimatedKcal: z.number().nullable(),
});
export const workoutTemplateInputSchema = z.object({
    name: z.string().min(1).max(120),
    type: workoutTypeSchema,
    durationMin: z.number().int().min(1).max(1440),
    intensity: intensitySchema.default('moderate'),
    rpe: z.number().int().min(1).max(10).nullish(),
    met: z.number().min(1).max(25).nullish(),
    notes: z.string().max(1000).nullish(),
});
/** Which edition of the Compendium of Physical Activities a MET comes from. */
export const compendiumSchema = z.enum(['adult', 'older_adult', 'wheelchair']);
/** One activity from the Compendium, with the citation it asks for. */
export const activityMetSchema = z.object({
    id: z.string().uuid(),
    compendium: compendiumSchema,
    code: z.string(),
    categoryCode: z.string(),
    category: z.string(),
    description: z.string(),
    met: z.number(),
    basis: z.string(),
    attribution: z.string(),
    /** Which of the eight kinds it is offered under; null when it is offered nowhere. */
    kind: workoutTypeSchema.nullable(),
});
export const trainingSlotSchema = z.object({
    id: z.string().uuid(),
    date: z.string(),
    memberId: z.string().uuid(),
    memberName: z.string(),
    workoutTemplateId: z.string().uuid().nullable(),
    name: z.string(),
    type: workoutTypeSchema,
    durationMin: z.number().int(),
    intensity: intensitySchema,
    status: z.enum(['planned', 'done', 'skipped']),
    linkedActivityId: z.string().uuid().nullable(),
    note: z.string().nullable(),
    /** Net of resting metabolism, which is what the day's balance uses. */
    estimatedKcal: z.number().nullable(),
    /** A session planned for somebody else is offered until they answer (ADR-016). */
    state: z.enum(['offered', 'accepted', 'declined']),
    /** The Compendium activity it is, when one was picked (ADR-033). */
    activityMet: activityMetSchema.nullable(),
});
export const trainingSlotInputSchema = z
    .object({
    date: z.string().date(),
    memberId: z.string().uuid(),
    workoutTemplateId: z.string().uuid().nullish(),
    custom: z
        .object({
        name: z.string().min(1).max(120),
        type: workoutTypeSchema,
        durationMin: z.number().int().min(1).max(1440),
        intensity: intensitySchema.default('moderate'),
    })
        .nullish(),
    /** The Compendium activity; the kind is then the activity's own (ADR-033). */
    activityMetId: z.string().uuid().nullish(),
    note: z.string().max(300).nullish(),
})
    .refine((v) => Boolean(v.workoutTemplateId) !== Boolean(v.custom), {
    message: 'A training slot is either a template or a one-off, not both',
});
export const activitySchema = z.object({
    id: z.string().uuid(),
    memberId: z.string().uuid(),
    memberName: z.string(),
    type: workoutTypeSchema,
    startedAt: z.date(),
    durationS: z.number().int(),
    distanceM: z.number().nullable(),
    energyKj: z.number().nullable(),
    energyKcal: z.number().nullable(),
    rpe: z.number().int().nullable(),
    notes: z.string().nullable(),
    trainingSlotId: z.string().uuid().nullable(),
    /** The Compendium activity the person said it was, if they have (ADR-027). */
    activityMet: z
        .object({
        id: z.string().uuid(),
        code: z.string(),
        description: z.string(),
        met: z.number(),
        attribution: z.string(),
    })
        .nullable(),
    /** True when the energy is ours, from a MET, rather than a device's. */
    energyEstimated: z.boolean(),
    /** Every device that recorded this session. */
    sources: z.array(z.object({
        id: z.string().uuid(),
        provider: syncProviderSchema,
        externalId: z.string().nullable(),
        durationS: z.number().int(),
        distanceM: z.number().nullable(),
        energyKj: z.number().nullable(),
        isPrimary: z.boolean(),
    })),
});
export const logActivitySchema = z.object({
    memberId: z.string().uuid(),
    type: workoutTypeSchema,
    startedAt: z.string().datetime(),
    durationMin: z.number().positive().max(1440),
    distanceKm: z.number().min(0).max(1000).nullish(),
    rpe: z.number().int().min(1).max(10).nullish(),
    intensity: intensitySchema.default('moderate'),
    notes: z.string().max(1000).nullish(),
    trainingSlotId: z.string().uuid().nullish(),
    /** A Compendium activity chosen by the person; its MET is used instead of the intensity table. */
    activityMetId: z.string().uuid().nullish(),
});
/**
 * Correct a recorded session. Every field is optional; a field left out is
 * left alone, and null clears one that can be empty. `activityMetId` says,
 * or unsays, which Compendium activity it was (ADR-027).
 */
export const updateActivitySchema = z
    .object({
    type: workoutTypeSchema.optional(),
    startedAt: z.string().datetime().optional(),
    durationMin: z.number().positive().max(1440).optional(),
    distanceKm: z.number().min(0).max(1000).nullable().optional(),
    rpe: z.number().int().min(1).max(10).nullable().optional(),
    notes: z.string().max(1000).nullable().optional(),
    activityMetId: z.string().uuid().nullable().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to change' });
/**
 * A session shape a person keeps repeating: the same type, length, distance
 * and effort, so it can be logged again in one tap. Intensity is not stored
 * on an activity; the client infers it from the effort.
 */
export const recentSessionSchema = z.object({
    type: workoutTypeSchema,
    durationMin: z.number().int(),
    distanceKm: z.number().nullable(),
    rpe: z.number().int().nullable(),
    uses: z.number().int(),
    lastAt: z.string().datetime(),
});
export const activityMetQuerySchema = z.object({
    q: z.string().max(120).optional(),
    compendium: compendiumSchema.optional(),
    category: z.string().max(4).optional(),
    /** Only the rows placed under this kind: the second tier of choice (ADR-027). */
    kind: workoutTypeSchema.optional(),
    /** Only rows offered for training at all, under any kind (ADR-033). */
    offered: z.enum(['true', 'false']).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const frequentActivityMetQuerySchema = z.object({
    memberId: z.string().uuid(),
    limit: z.coerce.number().int().min(1).max(20).default(8),
});
/**
 * An activity one person keeps picking, and the minutes they usually do,
 * topped up from a fixed starter set so the list is never empty (ADR-033).
 */
export const frequentActivityMetSchema = activityMetSchema.extend({
    /** How many times they picked it; 0 for a starter. */
    uses: z.number().int(),
    typicalMin: z.number().int().nullable(),
});
export const activityPreferencesSchema = z.object({
    preferredActivityProviders: z.array(syncProviderSchema).max(5).default([]),
    /** 0 means exercise does not raise the day's target; 1 means all of it. */
    eatBackFraction: z.number().min(0).max(1).default(0),
});
export const loadSummarySchema = z.object({
    memberId: z.string().uuid(),
    memberName: z.string(),
    weeklyLoad: z.number(),
    chronicLoad: z.number(),
    ratio: z.number().nullable(),
    trend: z.enum(['ramping', 'steady', 'easing', 'unknown']),
});
export const weeklyReviewSchema = z.object({
    weekStart: z.string(),
    members: z.array(z.object({
        memberId: z.string().uuid(),
        memberName: z.string(),
        meals: z.object({
            planned: z.number().int(),
            cooked: z.number().int(),
            logged: z.number().int(),
            adherence: z.number().nullable(),
        }),
        intake: z.object({
            meanEnergyKcal: z.number().nullable(),
            meanProteinG: z.number().nullable(),
            meanSugarG: z.number().nullable(),
            daysLogged: z.number().int(),
            targetEnergyKcal: z.number().nullable(),
            targetSugarG: z.number().nullable(),
        }),
        training: z.object({
            planned: z.number().int(),
            completed: z.number().int(),
            activityCount: z.number().int(),
            totalKcal: z.number().nullable(),
            load: loadSummarySchema.nullable(),
        }),
        weight: z.object({
            startKg: z.number().nullable(),
            endKg: z.number().nullable(),
            changeKg: z.number().nullable(),
        }),
        readings: z.object({
            meanSleepMin: z.number().int().nullable(),
            nights: z.number().int(),
            meanSteps: z.number().int().nullable(),
            days: z.number().int(),
        }),
    })),
});
export const stravaConnectSchema = z.object({ memberId: z.string().uuid() });
//# sourceMappingURL=training.js.map